/**
 * MusicWidget - Compact floating widget for audio control
 *
 * Used on Login/Register pages where ConfigModal is not available.
 * - Click: Toggle expand/collapse sliders
 * - Click outside or Escape: Close
 * NOTE: Uses position: relative. Wrap in FloatingControls for fixed positioning.
 */

import { useState, useRef, useEffect } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import Slider from '@components/ui/general/Slider';
import useConfigStore from '@/store/useConfigStore';
import './MusicWidget.css';

export default function MusicWidget() {
  const [isExpanded, setIsExpanded] = useState(false);
  const widgetRef = useRef(null);

  const settings = useConfigStore((state) => state.settings);
  const updateSetting = useConfigStore((state) => state.updateSetting);

  const musicVolume = settings.music_volume;
  const sfxVolume = settings.sfx_volume;
  const isMuted = musicVolume === 0 && sfxVolume === 0;

  // Click outside to close
  useEffect(() => {
    if (!isExpanded) return;
    function handleClickOutside(event) {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  // Escape to close
  useEffect(() => {
    if (!isExpanded) return;
    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsExpanded(false);
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      ref={widgetRef}
      className={`music-widget ${isExpanded ? 'music-widget--expanded' : ''}`}
    >
      <button
        className="button-square"
        onClick={handleToggle}
        title={isExpanded ? 'Close' : 'Audio settings'}
        type="button"
        data-sfx="button"
      >
        {isMuted ? <VolumeX /> : <Volume2 />}
      </button>

      <div className="music-widget__sliders">
        <div className="music-widget__slider-row">
          <span className="music-widget__label">Music</span>
          <Slider
            variant="mini"
            value={musicVolume}
            onChange={(val) => updateSetting('music_volume', val)}
            min={0}
            max={100}
          />
        </div>
        <div className="music-widget__slider-row">
          <span className="music-widget__label">SFX</span>
          <Slider
            variant="mini"
            value={sfxVolume}
            onChange={(val) => updateSetting('sfx_volume', val)}
            min={0}
            max={100}
          />
        </div>
      </div>
    </div>
  );
}
